
/**
 * Country-specific configurations for currency and phone formatting.
 */
export const COUNTRY_CONFIGS = {
    'India': {
        locale: 'en-IN',
        currency: 'INR',
        currencySymbol: '₹',
        phoneCode: '+91',
        phoneLength: 10,
        phoneRegex: /^\d{10}$/,
    },
    'United Arab Emirates': {
        locale: 'en-AE',
        currency: 'AED',
        currencySymbol: 'AED',
        phoneCode: '+971',
        phoneLength: 9,
        phoneRegex: /^\d{9}$/,
    },
    'Kuwait': {
        locale: 'en-KW',
        currency: 'KWD',
        currencySymbol: 'KWD',
        phoneCode: '+965',
        phoneLength: 8,
        phoneRegex: /^\d{8}$/,
    },
    'United Kingdom': {
        locale: 'en-GB',
        currency: 'GBP',
        currencySymbol: '£',
        phoneCode: '+44',
        phoneLength: 10,
        phoneRegex: /^\d{10}$/,
    },
    'United States': {
        locale: 'en-US',
        currency: 'USD',
        currencySymbol: '$',
        phoneCode: '+1',
        phoneLength: 10,
        phoneRegex: /^\d{10}$/,
    }
};

const DEFAULT_CONFIG = {
    locale: 'en-US',
    currency: 'USD',
    currencySymbol: '$',
    phoneCode: '',
    phoneLength: null,
    phoneRegex: null,
};

export const getCountryConfig = (countryName) => {
    return COUNTRY_CONFIGS[countryName] || DEFAULT_CONFIG;
};

let cachedCountries = [];
export const fetchCountriesConfig = async () => {
    try {
        const res = await fetch('/api/countries');
        if (res.ok) {
            cachedCountries = await res.json();
        }
    } catch (err) {
        console.error('Failed to fetch dynamic countries:', err);
    }
};

export const getDynamicCountry = (countryName) => {
    return cachedCountries.find(c => c.name === countryName) || null;
};

export const getCurrencyCode = (countryName) => {
    const cData = getDynamicCountry(countryName);
    if (cData) return (cData.engCurrency || 'USD').substring(0, 3).trim();
    return getCountryConfig(countryName).currency;
};

export const getCurrencySymbol = (countryName) => {
    const cData = getDynamicCountry(countryName);
    if (cData) return cData.currency || '$';
    return getCountryConfig(countryName).currencySymbol;
};

/**
 * Formats a number as currency based on the country.
 */
export const formatCurrency = (amount, countryName, dynamicCountryData = null) => {
    let config;
    const cData = dynamicCountryData || getDynamicCountry(countryName);
    
    if (cData) {
        const cCode = (cData.engCurrency || 'USD').substring(0, 3).trim();
        config = {
            locale: cData.locale || 'en-US',
            currency: cCode.length === 3 ? cCode : 'USD',
            currencySymbol: cData.currency || '$',
        };
    } else {
        config = getCountryConfig(countryName);
    }

    try {
        // Force Latin (Western) numerals by appending -u-nu-latn to any locale.
        // This prevents Arabic-Indic digits from showing for Kuwait and other Arabic regions.
        const baseLocale = (config.locale || 'en-US').split('-u-')[0];
        const safeLocale = `${baseLocale}-u-nu-latn`;
        return new Intl.NumberFormat(safeLocale, {
            style: 'currency',
            currency: config.currency,
            minimumFractionDigits: config.currency === 'KWD' ? 3 : 2,
        }).format(amount || 0);
    } catch (e) {
        return `${config.currencySymbol}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    }
};

/**
 * Formats a phone number based on the country.
 */
export const formatPhone = (phone, countryName) => {
    if (!phone) return '';
    const config = getCountryConfig(countryName);

    // Remove all non-numeric characters
    let cleaned = phone.toString().replace(/\D/g, '');

    // If it starts with the country code (without +), remove it for uniform processing
    const codeNoPlus = config.phoneCode.replace('+', '');
    if (codeNoPlus && cleaned.startsWith(codeNoPlus)) {
        cleaned = cleaned.slice(codeNoPlus.length);
    }

    // Re-apply country code and spacing
    if (countryName === 'India' && cleaned.length === 10) {
        return `${config.phoneCode} ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }

    if (config.phoneCode) {
        return `${config.phoneCode} ${cleaned}`;
    }

    // Fallback: if no country code, just return as is but maybe formatted a bit
    return phone;
};

/**
 * Validates a phone number based on the country.
 */
export const validatePhone = (phone, countryName) => {
    if (!phone) return false;
    const config = getCountryConfig(countryName);

    let cleaned = phone.toString().replace(/\D/g, '');
    const codeNoPlus = config.phoneCode.replace('+', '');

    if (codeNoPlus && cleaned.startsWith(codeNoPlus)) {
        cleaned = cleaned.slice(codeNoPlus.length);
    }

    if (config.phoneLength) {
        return cleaned.length === config.phoneLength;
    }

    return cleaned.length >= 8; // Basic fallback
};

/**
 * Strips formatting to get raw numeric value for phone.
 */
export const getRawPhone = (phone, countryName) => {
    const config = getCountryConfig(countryName);
    let cleaned = phone.toString().replace(/\D/g, '');
    const codeNoPlus = config.phoneCode.replace('+', '');

    if (codeNoPlus && cleaned.startsWith(codeNoPlus)) {
        cleaned = cleaned.slice(codeNoPlus.length);
    }
    return cleaned;
};

/**
 * Converts a number into words, handling specific currencies based on the country.
 */
export const numberToWordsCurrency = (amount, countryName) => {
    if (isNaN(amount) || amount === null) return '';
    const config = getCountryConfig(countryName);
    
    let currencyName = '';
    let fractionalName = '';
    
    if (config.currency === 'KWD') {
        currencyName = 'Kuwaiti Dinar';
        fractionalName = 'Fils';
    } else if (config.currency === 'INR') {
        currencyName = 'Rupees';
        fractionalName = 'Paise';
    } else if (config.currency === 'AED') {
        currencyName = 'Dirhams';
        fractionalName = 'Fils';
    } else if (config.currency === 'USD') {
        currencyName = 'Dollars';
        fractionalName = 'Cents';
    } else if (config.currency === 'GBP') {
        currencyName = 'Pounds';
        fractionalName = 'Pence';
    } else {
        currencyName = config.currency;
        fractionalName = 'Cents';
    }

    const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const convertTens = (num) => {
        if (num < 10) return units[num];
        if (num >= 10 && num < 20) return teens[num - 10];
        const t = Math.floor(num / 10);
        const u = num % 10;
        return tens[t] + (u > 0 ? " " + units[u] : "");
    };

    const convertHundreds = (num) => {
        if (num > 99) {
            const h = Math.floor(num / 100);
            const remainder = num % 100;
            return units[h] + " Hundred" + (remainder > 0 ? " " + convertTens(remainder) : "");
        }
        return convertTens(num);
    };

    const convertNumber = (num) => {
        if (num === 0) return "Zero";
        
        const b = Math.floor(num / 1000000000);
        let remainder = num % 1000000000;
        
        const m = Math.floor(remainder / 1000000);
        remainder = remainder % 1000000;
        
        const k = Math.floor(remainder / 1000);
        remainder = remainder % 1000;
        
        let result = "";
        if (b > 0) result += convertHundreds(b) + " Billion ";
        if (m > 0) result += convertHundreds(m) + " Million ";
        if (k > 0) result += convertHundreds(k) + " Thousand ";
        if (remainder > 0) result += convertHundreds(remainder);
        
        return result.trim();
    };

    const wholePart = Math.floor(amount);
    let fractionalPart = 0;
    
    if (config.currency === 'KWD') {
        fractionalPart = Math.round((amount - wholePart) * 1000);
    } else {
        fractionalPart = Math.round((amount - wholePart) * 100);
    }

    let words = `${convertNumber(wholePart)} ${currencyName}`;
    if (fractionalPart > 0) {
        words += ` And ${convertNumber(fractionalPart)} ${fractionalName}`;
    }
    
    return words + ' Only';
};
